import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { geohashForLocation, geohashQueryBounds, distanceBetween } from 'geofire-common';
import cardService, { CARD_RARITIES } from './cardService';

/**
 * Area Spawn Service - Manages treasure hunting system
 * Spawns hidden tiered notes and collectibles in a 500m radius around the user
 */
class AreaSpawnService {
  constructor() {
    this.SPAWN_RADIUS = 500; // meters
    this.TARGET_SPAWN_COUNT = 50;
    this.SPAWN_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours
    this.EARTH_MOTHER_ID = import.meta.env.VITE_EARTH_MOTHER;

    // Distance bands for even distribution (in meters)
    this.DISTRIBUTION_BANDS = [
      { min: 0, max: 100, count: 15 },    // 30% within 100m
      { min: 100, max: 250, count: 20 },  // 40% within 100-250m
      { min: 250, max: 500, count: 15 },  // 30% within 250-500m
    ];
  }

  getDb() {
    return firebase.firestore();
  }

  /**
   * Initialize spawns for a user's area on login
   * @param {Object} userLocation - { lat, lng }
   * @returns {Promise<number>} - Number of spawns created
   */
  async initializeUserArea(userLocation) {
    if (!userLocation) {
      console.error('No user location provided');
      return 0;
    }

    try {
      // Count existing active spawns in the area
      const existingSpawns = await this.getActiveSpawnsInArea(userLocation);
      const spawnCount = existingSpawns.length;

      console.log(`Found ${spawnCount} existing spawns in area`);

      if (spawnCount < this.TARGET_SPAWN_COUNT) {
        const spawnsNeeded = this.TARGET_SPAWN_COUNT - spawnCount;
        console.log(`Creating ${spawnsNeeded} new spawns...`);
        await this.createSpawns(userLocation, spawnsNeeded);
        return spawnsNeeded;
      }

      return 0;
    } catch (error) {
      console.error('Error initializing user area:', error);
      return 0;
    }
  }

  /**
   * Get all active (unclaimed, unexpired) spawns in area
   * @param {Object} location - { lat, lng }
   * @returns {Promise<Array>} - Array of spawn documents
   */
  async getActiveSpawnsInArea(location) {
    const db = this.getDb();
    const center = [location.lat, location.lng];
    const radiusInM = this.SPAWN_RADIUS;

    // Generate geohash query bounds
    const bounds = geohashQueryBounds(center, radiusInM);
    const promises = [];

    for (const b of bounds) {
      const q = db.collection('areaSpawns')
        .where('geohash', '>=', b[0])
        .where('geohash', '<=', b[1])
        .where('claimed', '==', false)
        .where('expiresAt', '>', new Date());

      promises.push(q.get());
    }

    const snapshots = await Promise.all(promises);
    const matchingDocs = [];

    for (const snap of snapshots) {
      for (const doc of snap.docs) {
        const data = doc.data();
        const spawnLocation = [data.location.lat, data.location.lng];

        // Verify distance
        const distanceInM = distanceBetween(spawnLocation, center);
        if (distanceInM <= radiusInM) {
          matchingDocs.push({
            id: doc.id,
            ...data,
          });
        }
      }
    }

    return matchingDocs;
  }

  /**
   * Create spawns with even distribution across distance bands
   * @param {Object} userLocation - { lat, lng }
   * @param {number} totalCount - Total spawns to create
   */
  async createSpawns(userLocation, totalCount) {
    const db = this.getDb();
    const batch = db.batch();
    let batchCount = 0;
    const maxBatchSize = 500;

    // Calculate spawns per band based on target count
    const spawnDistribution = this.DISTRIBUTION_BANDS.map(band => ({
      ...band,
      spawnCount: Math.floor((band.count / 50) * totalCount),
    }));

    // Ensure we create exactly totalCount spawns
    const distributedTotal = spawnDistribution.reduce((sum, band) => sum + band.spawnCount, 0);
    if (distributedTotal < totalCount) {
      spawnDistribution[0].spawnCount += (totalCount - distributedTotal);
    }

    for (const band of spawnDistribution) {
      for (let i = 0; i < band.spawnCount; i++) {
        // Generate random location within band
        const spawnLocation = this.getRandomLocationInBand(userLocation, band.min, band.max);

        // Determine what to spawn (50% tiered notes, 50% collectibles)
        const isCollectible = Math.random() < 0.5;
        const spawnData = isCollectible
          ? await this.generateCollectibleSpawn(spawnLocation)
          : this.generateTieredNoteSpawn(spawnLocation);

        const spawnRef = db.collection('areaSpawns').doc();
        batch.set(spawnRef, spawnData);

        batchCount++;

        // Commit batch if it reaches max size
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }

    // Commit remaining
    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(`Created ${totalCount} spawns distributed across area`);
  }

  /**
   * Generate a random location within a distance band
   * @param {Object} center - { lat, lng }
   * @param {number} minDistance - Minimum distance in meters
   * @param {number} maxDistance - Maximum distance in meters
   * @returns {Object} - { lat, lng }
   */
  getRandomLocationInBand(center, minDistance, maxDistance) {
    // Random angle in radians
    const angle = Math.random() * 2 * Math.PI;

    // Random distance within band (sqrt for uniform distribution)
    const minDistanceDeg = minDistance / 111320;
    const maxDistanceDeg = maxDistance / 111320;
    const distance = Math.sqrt(
      Math.random() * (maxDistanceDeg * maxDistanceDeg - minDistanceDeg * minDistanceDeg)
      + minDistanceDeg * minDistanceDeg
    );

    return {
      lat: center.lat + distance * Math.cos(angle),
      lng: center.lng + distance * Math.sin(angle),
    };
  }

  /**
   * Generate tiered note spawn data using existing probability formulas
   * @param {Object} location - { lat, lng }
   * @returns {Object} - Spawn document data
   */
  generateTieredNoteSpawn(location) {
    // Use the same tier probabilities as noteSpawnService
    const tiers = [
      { name: 'copper', chance: 0.60, points: 1 },      // 60%
      { name: 'nickel', chance: 0.25, points: 2 },      // 25%
      { name: 'bronze', chance: 0.10, points: 10 },     // 10%
      { name: 'silver', chance: 0.04, points: 25 },     // 4%
      { name: 'gold', chance: 0.0095, points: 100 },    // 0.95%
      { name: 'platinum', chance: 0.0005, points: 1000 }, // 0.05%
    ];

    // Roll for tier
    const rand = Math.random();
    let selectedTier = tiers[0]; // Default to copper
    let cumulative = 0;

    for (const tier of tiers) {
      cumulative += tier.chance;
      if (rand <= cumulative) {
        selectedTier = tier;
        break;
      }
    }

    const geohash = geohashForLocation([location.lat, location.lng]);

    return {
      location: {
        lat: location.lat,
        lng: location.lng,
      },
      geohash,
      type: 'tieredNote',
      tier: selectedTier.name,
      pointValue: selectedTier.points,
      claimed: false,
      claimedBy: null,
      claimedAt: null,
      spawnedAt: firebase.firestore.FieldValue.serverTimestamp(),
      expiresAt: new Date(Date.now() + this.SPAWN_EXPIRATION),
      spawnedBy: 'system',
    };
  }

  /**
   * Generate collectible card spawn data using card rarity system
   * @param {Object} location - { lat, lng }
   * @returns {Promise<Object>} - Spawn document data
   */
  async generateCollectibleSpawn(location) {
    // Get a random card from set1 using existing card service
    const randomCard = await cardService.getRandomCard('set1');

    if (!randomCard) {
      console.error('Failed to get random card for spawn');
      // Fallback to copper note
      return this.generateTieredNoteSpawn(location);
    }

    const geohash = geohashForLocation([location.lat, location.lng]);

    return {
      location: {
        lat: location.lat,
        lng: location.lng,
      },
      geohash,
      type: 'collectible',
      cardId: randomCard.cardId,
      cardNumber: randomCard.cardNumber,
      setId: randomCard.setId,
      rarity: randomCard.rarity,
      name: randomCard.name,
      description: randomCard.description,
      imageUrl: randomCard.imageUrl,
      claimed: false,
      claimedBy: null,
      claimedAt: null,
      spawnedAt: firebase.firestore.FieldValue.serverTimestamp(),
      expiresAt: new Date(Date.now() + this.SPAWN_EXPIRATION),
      spawnedBy: 'system',
    };
  }

  /**
   * Replenish spawns after an item is claimed (event-driven)
   * @param {Object} userLocation - { lat, lng }
   */
  async replenishSpawns(userLocation) {
    try {
      const existingSpawns = await this.getActiveSpawnsInArea(userLocation);
      const spawnCount = existingSpawns.length;

      if (spawnCount < this.TARGET_SPAWN_COUNT) {
        const spawnsNeeded = this.TARGET_SPAWN_COUNT - spawnCount;
        console.log(`Replenishing ${spawnsNeeded} spawns after claim...`);
        await this.createSpawns(userLocation, spawnsNeeded);
      }
    } catch (error) {
      console.error('Error replenishing spawns:', error);
    }
  }

  /**
   * Claim a spawn and add to user's inventory
   * @param {string} spawnId - The spawn document ID
   * @param {string} userId - The user claiming the spawn
   * @param {Object} userLocation - Current user location for replenishment
   * @returns {Promise<Object>} - Claim result
   */
  async claimSpawn(spawnId, userId, userLocation) {
    const db = this.getDb();
    const spawnRef = db.collection('areaSpawns').doc(spawnId);

    try {
      const result = await db.runTransaction(async (transaction) => {
        const spawnDoc = await transaction.get(spawnRef);

        if (!spawnDoc.exists) {
          throw new Error('Spawn not found');
        }

        const spawnData = spawnDoc.data();

        if (spawnData.claimed) {
          throw new Error('Already claimed by another user');
        }

        if (spawnData.expiresAt.toDate() < new Date()) {
          throw new Error('Spawn has expired');
        }

        // Mark as claimed
        transaction.update(spawnRef, {
          claimed: true,
          claimedBy: userId,
          claimedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        return {
          success: true,
          spawn: spawnData,
        };
      });

      // Trigger replenishment after successful claim
      if (result.success) {
        // Don't await - let it run in background
        this.replenishSpawns(userLocation).catch(err =>
          console.error('Background replenishment failed:', err)
        );
      }

      return result;
    } catch (error) {
      console.error('Error claiming spawn:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get spawns within detection radius (for radar)
   * @param {Object} location - { lat, lng }
   * @param {number} radiusMeters - Detection radius
   * @returns {Promise<Array>} - Array of nearby spawns
   */
  async getSpawnsInRadius(location, radiusMeters = 100) {
    const db = this.getDb();
    const center = [location.lat, location.lng];
    const bounds = geohashQueryBounds(center, radiusMeters);
    const promises = [];

    for (const b of bounds) {
      const q = db.collection('areaSpawns')
        .where('geohash', '>=', b[0])
        .where('geohash', '<=', b[1])
        .where('claimed', '==', false)
        .where('expiresAt', '>', new Date());

      promises.push(q.get());
    }

    const snapshots = await Promise.all(promises);
    const matchingDocs = [];

    for (const snap of snapshots) {
      for (const doc of snap.docs) {
        const data = doc.data();
        const spawnLocation = [data.location.lat, data.location.lng];

        const distanceInM = distanceBetween(spawnLocation, center);
        if (distanceInM <= radiusMeters) {
          matchingDocs.push({
            id: doc.id,
            ...data,
            distance: distanceInM,
          });
        }
      }
    }

    return matchingDocs;
  }

  /**
   * Prospect an area (search for hidden items)
   * @param {Object} searchLocation - { lat, lng } where user clicked
   * @param {Object} userLocation - { lat, lng } current user position
   * @param {number} searchRadius - Search radius in meters (default 5m)
   * @returns {Promise<Object>} - Prospect result
   */
  async prospectArea(searchLocation, userLocation, searchRadius = 5) {
    const center = [searchLocation.lat, searchLocation.lng];
    const userPos = [userLocation.lat, userLocation.lng];

    // Check if search location is within 200m of user
    // distanceBetween returns kilometers, so convert to meters
    const distanceToSearchKm = distanceBetween(userPos, center);
    const distanceToSearchM = distanceToSearchKm * 1000;

    if (distanceToSearchM > 200) {
      return {
        success: false,
        message: "I'll need to get closer to search that area",
        tooFar: true,
      };
    }

    // Find spawns within search radius
    const nearbySpawns = await this.getSpawnsInRadius(searchLocation, searchRadius);

    if (nearbySpawns.length === 0) {
      // Nothing in the area
      const messages = [
        "Doesn't seem to be anything here",
        "Nothing detected in this area",
        "The scanner is coming up empty",
        "No signals detected here",
      ];
      return {
        success: false,
        message: messages[Math.floor(Math.random() * messages.length)],
        nothingFound: true,
      };
    }

    // There's something here - roll for success (10% chance)
    const SUCCESS_CHANCE = 0.10;
    const foundItem = Math.random() < SUCCESS_CHANCE;

    if (foundItem) {
      // Return the closest spawn
      const closestSpawn = nearbySpawns.sort((a, b) => a.distance - b.distance)[0];
      return {
        success: true,
        found: true,
        spawn: closestSpawn,
        message: "Found something!",
      };
    } else {
      // Near miss - something is here but didn't find it
      const messages = [
        "Hmm, nothing found, but I'm certain something is here",
        "My device is picking up a signal, but I can't locate it",
        "There's definitely something nearby, just need to keep searching",
        "The scanner indicates something close, try again",
      ];
      return {
        success: false,
        found: false,
        message: messages[Math.floor(Math.random() * messages.length)],
        nearMiss: true,
      };
    }
  }
}

const areaSpawnService = new AreaSpawnService();
export default areaSpawnService;
